import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchServiceConfigById, updateServiceConfig, deleteServiceConfig } from '../store/slices/serviceConfigSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { resolveServiceId, KNOWN_SERVICES, MODES, ConfigMode } from '../api/serviceConfigApi';
import { FiArrowLeft, FiEdit2, FiSave, FiX, FiTrash2, FiPlus } from 'react-icons/fi';

const SYSTEM_KEYS = new Set(['_id', 'serviceId', 'serviceName', 'mode', 'subscribedTopics', 'createdAt', 'updatedAt', '__v']);

const ServiceConfigDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { selectedConfig, loading } = useSelector((state: RootState) => state.serviceConfigs);

    const [isEditing, setIsEditing] = useState(false);
    const [serviceId, setServiceId] = useState('');
    const [mode, setMode] = useState<ConfigMode>('default');
    const [topics, setTopics] = useState<string[]>([]);
    const [newTopic, setNewTopic] = useState('');
    const [extraJson, setExtraJson] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (id) dispatch(fetchServiceConfigById(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (selectedConfig && !isEditing) {
            setServiceId(resolveServiceId(selectedConfig));
            setMode(selectedConfig.mode as ConfigMode);
            setTopics(selectedConfig.subscribedTopics ?? []);

            // Build extra JSON from remaining keys
            const extra: Record<string, any> = {};
            for (const [k, v] of Object.entries(selectedConfig)) {
                if (!SYSTEM_KEYS.has(k)) extra[k] = v;
            }
            setExtraJson(Object.keys(extra).length > 0 ? JSON.stringify(extra, null, 2) : '');
        }
    }, [selectedConfig]);

    const validateJson = (v: string) => {
        if (!v.trim()) { setJsonError(''); return true; }
        try { JSON.parse(v); setJsonError(''); return true; }
        catch (e: any) { setJsonError(e.message); return false; }
    };

    const handleSave = async () => {
        if (!id || !validateJson(extraJson)) return;
        setSaving(true);
        try {
            const extra = extraJson.trim() ? JSON.parse(extraJson) : {};
            await dispatch(updateServiceConfig({
                id,
                data: {
                    serviceId,
                    mode,
                    subscribedTopics: topics.length > 0 ? topics : undefined,
                    ...extra,
                },
            })).unwrap();
            dispatch(addNotification({ type: 'success', message: 'Config updated.' }));
            setIsEditing(false);
        } catch (e: any) {
            dispatch(addNotification({ type: 'error', message: e.message }));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        if (!id || !selectedConfig) return;
        dispatch(showModal({
            title: 'Delete Service Config',
            content: `Delete "${resolveServiceId(selectedConfig)}:${selectedConfig.mode}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                await dispatch(deleteServiceConfig(id)).unwrap();
                dispatch(addNotification({ type: 'success', message: 'Config deleted.' }));
                navigate('/service-configs');
            },
        }));
    };

    if (loading && !selectedConfig) return (
        <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
        </div>
    );
    if (!selectedConfig) return null;

    const extraKeys = Object.keys(selectedConfig).filter(k => !SYSTEM_KEYS.has(k));
    const label = `${resolveServiceId(selectedConfig)}:${selectedConfig.mode}`;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/service-configs')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                        <FiArrowLeft className="mr-2" /> Back
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white font-mono">{label}</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Updated {new Date(selectedConfig.updatedAt).toLocaleString()}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {!isEditing ? (
                        <>
                            <button onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm rounded-md text-white bg-primary-600 hover:bg-primary-700">
                                <FiEdit2 className="mr-2" /> Edit
                            </button>
                            <button onClick={handleDelete}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm rounded-md text-white bg-red-600 hover:bg-red-700">
                                <FiTrash2 className="mr-2" /> Delete
                            </button>
                        </>
                    ) : (
                        <button onClick={() => { setIsEditing(false); }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                            <FiX className="mr-2" /> Cancel
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-6">
                {isEditing ? (
                    <>
                        {/* Identity */}
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Identity</h3>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Service ID</label>
                                    <input type="text" value={serviceId} onChange={e => setServiceId(e.target.value)}
                                        list="known-services"
                                        className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                    <datalist id="known-services">
                                        {KNOWN_SERVICES.map(s => <option key={s} value={s} />)}
                                    </datalist>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mode</label>
                                    <select value={mode} onChange={e => setMode(e.target.value as ConfigMode)}
                                        className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                        {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Topics */}
                        <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Subscribed Topics</h3>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {topics.map((t, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-mono">
                                        {t}
                                        <button type="button" onClick={() => setTopics(topics.filter((_, j) => j !== i))}
                                            className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input type="text" value={newTopic} onChange={e => setNewTopic(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && newTopic.trim()) { setTopics([...topics, newTopic.trim()]); setNewTopic(''); } }}
                                    placeholder="topic-name (Enter to add)"
                                    className="block flex-1 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                <button type="button" onClick={() => { if (newTopic.trim()) { setTopics([...topics, newTopic.trim()]); setNewTopic(''); } }}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                                    <FiPlus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Extra JSON */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Additional Configuration</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                All sections except serviceId, mode, and subscribedTopics.
                            </p>
                            <textarea value={extraJson} onChange={e => { setExtraJson(e.target.value); validateJson(e.target.value); }}
                                rows={20} spellCheck={false}
                                className={`w-full font-mono text-xs border rounded-md p-3 dark:bg-gray-900 dark:text-gray-100 ${jsonError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} />
                            {jsonError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{jsonError}</p>}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button onClick={() => setIsEditing(false)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button onClick={handleSave} disabled={saving || !!jsonError}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                                {saving ? 'Saving…' : <><FiSave className="mr-2" />Save Changes</>}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Read-only view */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-5 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Service</dt>
                                <dd className="mt-1 text-sm font-mono font-semibold text-gray-900 dark:text-white">{resolveServiceId(selectedConfig)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Mode</dt>
                                <dd className="mt-1">
                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                        {selectedConfig.mode}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Sections</dt>
                                <dd className="mt-1 flex flex-wrap gap-1">
                                    {extraKeys.map(k => (
                                        <span key={k} className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-mono">{k}</span>
                                    ))}
                                    {extraKeys.length === 0 && <span className="text-xs text-gray-400">—</span>}
                                </dd>
                            </div>
                        </div>

                        {(selectedConfig.subscribedTopics?.length ?? 0) > 0 && (
                            <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subscribed Topics</h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedConfig.subscribedTopics!.map((t, i) => (
                                        <span key={i} className="px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-mono">{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {extraKeys.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Full Configuration</h3>
                                <pre className="bg-gray-50 dark:bg-gray-900 rounded-md p-4 text-xs font-mono overflow-auto max-h-[600px] text-gray-800 dark:text-gray-200">
                                    {JSON.stringify(
                                        Object.fromEntries(extraKeys.map(k => [k, (selectedConfig as any)[k]])),
                                        null, 2
                                    )}
                                </pre>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ServiceConfigDetailsPage;
